// Foreva Jewels — diamond certification add-on (used by blocks/fj-certification.liquid).
//
// When a certification is chosen, the product form's submit is taken over so the product and the
// certification are added to the cart in ONE request (both lines or neither). The theme's cart drawer
// and cart icon are then updated through the standard cart event, the same way the theme's own
// add-to-cart does it (assets/product-form.js). With no certification chosen, nothing here runs and
// the theme handles the add as usual.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SELECT = '[data-fj-cert-select]';

/** @param {HTMLFormElement} form */
function certSelectFor(form) {
  // Not form.id: the product form has an <input name="id"> (the variant), which shadows that property.
  const formId = form.getAttribute('id');
  if (!formId) return null;
  return /** @type {HTMLSelectElement | null} */ (document.querySelector(`${SELECT}[data-form-id="${CSS.escape(formId)}"]`));
}

/**
 * Keep the hidden "Certification" property on the main line in sync with the select, and move the
 * "Ship by" date (blocks/shipping-info.liquid) by the certification's extra days. Each option carries
 * its own date, computed in Liquid with the same rule as the shipping block.
 */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches(SELECT)) return;
  const option = select.selectedOptions[0];
  const wrap = select.closest('.fj-cert-wrap');
  const label = /** @type {HTMLInputElement | null} */ (wrap?.querySelector('[data-fj-cert-label]'));
  if (label) label.value = option?.dataset.label || '';

  const scope = select.closest('.shopify-section') || document;
  const shipDate = scope.querySelector('[data-fj-ship-date]');
  if (shipDate && option?.dataset.shipDate) {
    shipDate.textContent = option.dataset.shipDate;
    shipDate.classList.remove('fj-ship-flash');
    void shipDate.offsetWidth;
    shipDate.classList.add('fj-ship-flash');
  }
});

// The browser can restore a previous choice on back/forward navigation: sync the date for it.
document.querySelectorAll(SELECT).forEach((select) => {
  if (select instanceof HTMLSelectElement && select.value) select.dispatchEvent(new Event('change', { bubbles: true }));
});

/**
 * Window, capture phase: the theme routes on:submit through its own capture listener on document
 * (assets/component.js), so listening on window is what runs before it and can stop it.
 */
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const select = certSelectFor(form);
    if (!select || !select.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addWithCertification(form, select);
  },
  true
);

/**
 * @param {HTMLFormElement} form
 * @param {HTMLSelectElement} select
 */
async function addWithCertification(form, select) {
  const component = form.closest('product-form-component') || form;
  const errorEl = /** @type {HTMLElement | null} */ (select.closest('.fj-cert-wrap')?.querySelector('[data-fj-cert-error]'));
  if (errorEl) errorEl.hidden = true;

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;
  const variantId = String(formData.get('id') || '');

  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string' && value) properties[match[1]] = value;
  }

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  component.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        { merchandiseId: variantId, quantity },
        { merchandiseId: select.value, quantity },
      ],
      promise: deferred.promise,
    })
  );

  const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (form.querySelectorAll('button[type="submit"], button[name="add"]'));
  buttons.forEach((b) => (b.disabled = true));

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          { id: Number(variantId), quantity, properties },
          {
            id: Number(select.value),
            quantity,
            properties: { Certifies: select.dataset.certifies || '', _fj_cert: '1' },
          },
        ],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const json = await response.json();

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();

    if (json.status) {
      showError(errorEl, json.description || json.message);
      component.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'fj-certification' },
      });
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'fj-certification',
        sourceId: component.id || '',
        itemCount: quantity * 2,
        productId: component instanceof HTMLElement ? component.dataset.productId : undefined,
        sections: json.sections,
        didError: false,
      },
    });
  } catch (error) {
    console.error('[fj-certification]', error);
    deferred.reject(error);
    showError(errorEl, 'Something went wrong. Please try again.');
    component.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    buttons.forEach((b) => (b.disabled = false));
  }
}

/**
 * @param {HTMLElement | null} el
 * @param {string} message
 */
function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
}
